import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navItems = [
    { key: 'about', href: '#about' },
    { key: 'services', href: '#services' },
    { key: 'projects', href: '#projects' },
    { key: 'process', href: '#process' },
    { key: 'contact', href: '#contact' },
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };
  
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-xl border-b-2 border-primary/20 shadow-2xl shadow-primary/5' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom py-6">
        <div className="flex items-center justify-between">
          {/* Logo redesenhado */}
          <a
            href="#hero"
            className="flex items-center gap-3 group"
            onClick={(e) => handleNavClick(e, '#hero')}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center font-black text-background text-lg shadow-lg group-hover:rotate-12 transition-transform duration-300">
                B
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-pulse" />
            </div>
            <span className="text-2xl font-black text-white tracking-tight">
              balielo<span className="text-primary">.dev</span>
            </span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="relative px-5 py-2.5 text-gray-300 hover:text-white transition-all duration-300 font-bold text-sm uppercase tracking-wider group"
              >
                {t(`nav.${item.key}`)}
                <span className="absolute bottom-0 left-0 w-0 h-1 bg-primary rounded-full transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            
            {/* Language Switcher integrado */}
            <div className="ml-4 pl-4 border-l-2 border-white/10">
              <LanguageSwitcher />
            </div>
          </div>
          
          {/* Mobile Menu Button redesenhado */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-3 rounded-xl bg-primary/10 hover:bg-primary/20 border border-primary/30 transition-all"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-primary" />
            ) : (
              <Menu className="w-6 h-6 text-primary" />
            )}
          </button>
        </div>
        
        {/* Mobile Menu redesenhado */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
              <div className="flex flex-col gap-3 py-8 mt-6 border-t-2 border-primary/20 bg-background/50 backdrop-blur-sm rounded-2xl px-6">
                {navItems.map((item, index) => (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="flex items-center gap-4 text-gray-300 hover:text-white transition-all duration-300 font-bold text-lg py-3 px-4 rounded-xl hover:bg-primary/10 border-l-4 border-transparent hover:border-primary"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span className="text-primary font-black">{String(index + 1).padStart(2, '0')}</span>
                    {t(`nav.${item.key}`)}
                  </a>
                ))}
                
                {/* Language Switcher - Mobile */}
                <div className="pt-6 mt-6 border-t-2 border-white/10">
                  <LanguageSwitcher />
                </div>
              </div>
            </div>
          )}
      </nav>
    </header>
  );
};

export default Header;
